import { computed } from 'mobx'
import { map, type Observable } from 'rxjs'
import type { SessionOptions } from '@/features/iam/auth/SessionOptions'
import {
  type ExitMethod,
  NavigateToDashboardExitMethod,
  ReturnToPartnerExitMethod,
} from '@/features/shell/exiting/ExitMethod'
import type { NavigateFn } from '@/RootStore'
import { fromStream } from '@/utils/mobx-util'
import { startWith } from 'rxjs/operators'
import { shareLatest } from '@/utils/rx-util'

/**
 * Manages the exit method (what the various "Exit"/"Back" buttons do).
 *
 * How to exit can vary based on context. For example, magic link users
 * with a configured return URL will want to return the user to their site/app,
 * while regular B2C users just navigate back to the dashboard.
 */
export class ExitMethodStore {
  private readonly _getExitMethods

  /**
   * Observable that emits the exit methods to use based on the session.
   */
  readonly exitMethods$

  constructor(
    navigate: NavigateFn,
    sessionOptions$: Observable<SessionOptions>,
  ) {
    const defaultNavigateMethod = new NavigateToDashboardExitMethod(navigate)

    // Derive the exit methods based on the session options.
    const initial = {
      rootExitMethod: defaultNavigateMethod,
      pageExitMethod: defaultNavigateMethod,
    }

    this.exitMethods$ = sessionOptions$.pipe(
      map((options) => {
        // Default to the dashboard navigation
        let rootExitMethod: ExitMethod = defaultNavigateMethod
        let pageExitMethod: ExitMethod = defaultNavigateMethod

        // If the session has any return options, use that.
        const returnOptions = options.returnOptions
        if (returnOptions) {
          rootExitMethod = new ReturnToPartnerExitMethod(() => ({
            returnLabel: returnOptions.actionLabel,
            returnUrl: returnOptions.url,
            // If hiding the navbar, we'll treat that as the partner wanting to
            // control all exit UI themselves.
            isVisible: options.navbarOptions.kind !== 'None',
          }))

          // If the destination is anything but the dashboard AND we're hiding
          // the nav bar, it means we're embedding a specific view which the
          // user is not allowed to escape from.
          // This means we don't want them to navigate elsewhere in the
          // app, like the dashboard or account screen.
          if (
            options.destination.type !== 'Dashboard' &&
            options.navbarOptions.kind === 'None'
          ) {
            // Use the "return to partner" method, but hide it from the UI.
            pageExitMethod = new ReturnToPartnerExitMethod(() => ({
              returnLabel: returnOptions.actionLabel,
              returnUrl: returnOptions.url,
              isVisible: false,
            }))
          }
        }

        return {
          rootExitMethod,
          pageExitMethod,
        }
      }),
      startWith(initial),
      shareLatest(),
    )

    this._getExitMethods = fromStream(initial, this.exitMethods$)
  }

  /**
   * The exit method to use on the root view (for example, the dashboard).
   */
  @computed get rootExitMethod() {
    return this._getExitMethods().rootExitMethod
  }

  /**
   * The exit method to use on a non-root page.
   */
  @computed get pageExitMethod() {
    return this._getExitMethods().pageExitMethod
  }
}
